import React, { useEffect } from 'react';
import IFund from '../../interfaces/fund/IFund';
import IFundType from '../../interfaces/expertise/IFundType';
import VInvestExpertiseSynthesisPrivateCredit from './VInvestExpertiseSynthesisPrivateCredit';
import IInvestExpertiseFiltersResult from '../../interfaces/expertise/IInvestExpertiseFiltersResult';
import AppLoaderContent from '../../../../app/features/loader/content/AppLoaderContent';
import VInvestExpertiseSynthesisVentureCapital from './VInvestExpertiseSynthesisVentureCapital';
import VInvestExpertiseSynthesisPrivateEquity from './VInvestExpertiseSynthesisPrivateEquity';
import { Box } from '@mui/material';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';
import VInvestExpertiseSynthesisListedAssets from './VInvestExpertiseSynthesisListedAssets';
import * as XLSX from 'xlsx';
import removeAccents from 'remove-accents-esm';
import _ from 'lodash';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import investExpertiseListedAssetsLang from '../../langs/investExpertiseListedAssetsLang';
import useInvestFunds from '../../hooks/fund/useInvestFunds';
import { langLookup } from '../../../../app/utils/langLookup';

type VInvestExpertiseSynthesisProps = {
	investExpertise: IFundType;
	allFunds: IFund[];
	resultData: IInvestExpertiseFiltersResult;
	currency: TInvestFundCurrency;
	callExportData: boolean;
	resetCallExportData: () => void;
};

const VInvestExpertiseSynthesis = (props: VInvestExpertiseSynthesisProps) => {
	const { investExpertise, allFunds, resultData, currency, callExportData, resetCallExportData } = props;
	const auth = useAuthContext();
	const { t } = useTranslation();
	const { fundsByKey } = useInvestFunds();
	useEffect(() => {
		if (callExportData && resultData && auth && fundsByKey) {
			if (investExpertise.tag === 'listed_assets') {
				const company = removeAccents(auth.currentCompany?.name ?? '');
				const fileName = `${company} - ${t(investExpertiseListedAssetsLang.listedAssets.tag)}.xlsx`;
				const kpis = _.cloneDeep(resultData.kpisByFundForSiennaGestion ?? []).map((x) => ({
					fundName: x.fund?.name ?? '',
					aum: x.kpi?.aum ?? '',
					navPerShare: x.kpi?.navPerShare ?? '',
					perf1: x.kpi?.synthesisCumulativePerformance?.[0]?.value ?? '',
					perf2: x.kpi?.synthesisCumulativePerformance?.[1]?.value ?? '',
					perf3: x.kpi?.synthesisCumulativePerformance?.[2]?.value ?? '',
					perf4: x.kpi?.synthesisCumulativePerformance?.[3]?.value ?? '',
					perf5: x.kpi?.synthesisCumulativePerformance?.[4]?.value ?? '',
					classificationAMF:
						(x.fund?.id !== undefined && x.fund?.id !== null && fundsByKey[x.fund.id]?.classificationAMF) ||
						'',
					accreditationDate:
						(x.fund?.id !== undefined && x.fund?.id !== null && fundsByKey[x.fund.id]?.accreditationDate) ||
						'',
					labels:
						(x.fund?.id !== undefined && x.fund?.id !== null && fundsByKey[x.fund.id]?.labels?.join(',')) ||
						'',
				}));

				const perfsHeaders =
					resultData.kpisByFundForSiennaGestion?.[0]?.kpi?.synthesisCumulativePerformance?.map(
						(x) => x.dateType,
					) ?? [];

				const headers = [
					t(investExpertiseListedAssetsLang.fundName.tag),
					t(investExpertiseListedAssetsLang.header_total_net_assets.tag),
					t(investExpertiseListedAssetsLang.header_vl.tag),
					`Perf ${t(langLookup(investExpertiseListedAssetsLang, perfsHeaders[0])?.tag ?? '')}`,
					`Perf ${t(langLookup(investExpertiseListedAssetsLang, perfsHeaders[1])?.tag ?? '')}`,
					`Perf ${t(langLookup(investExpertiseListedAssetsLang, perfsHeaders[2])?.tag ?? '')}`,
					`Perf ${t(langLookup(investExpertiseListedAssetsLang, perfsHeaders[3])?.tag ?? '')}`,
					`Perf ${t(langLookup(investExpertiseListedAssetsLang, perfsHeaders[4])?.tag ?? '')}`,
					t(investExpertiseListedAssetsLang.header_classification.tag),
					t(investExpertiseListedAssetsLang.header_agreement_date.tag),
					t(investExpertiseListedAssetsLang.labels.tag),
				];

				const worksheet = XLSX.utils.json_to_sheet(kpis);
				const workbook = XLSX.utils.book_new();
				XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
				XLSX.utils.book_append_sheet(workbook, worksheet, 'Portfolio');

				worksheet['!cols'] = _.keys(kpis[0]).map((field, index) => {
					const header = headers[index] ?? '';
					return {
						wch: Math.max(
							header.length,
							Math.max(...kpis.map((obj: Record<string, unknown>) => String(obj[field] ?? '').length)),
						),
					};
				});
				XLSX.writeFileXLSX(workbook, fileName);
			}
		}
		resetCallExportData();
	}, [auth, callExportData, fundsByKey, investExpertise.tag, resetCallExportData, resultData, t]);

	return (
		<Box id="expertise-content-synthesis">
			{investExpertise ? (
				<>
					{investExpertise.tag === 'private_credit' ? (
						resultData && resultData.kpisByFund && resultData.kpis ? (
							<VInvestExpertiseSynthesisPrivateCredit
								allFunds={allFunds}
								kpisByFund={resultData.kpisByFund}
								kpis={resultData.kpis}
								currency={currency}
							/>
						) : (
							<AppLoaderContent />
						)
					) : investExpertise.tag === 'venture_capital' ? (
						<VInvestExpertiseSynthesisVentureCapital resultData={resultData} />
					) : investExpertise.tag === 'private_equity' ? (
						<VInvestExpertiseSynthesisPrivateEquity resultData={resultData} />
					) : investExpertise.tag === 'listed_assets' ? (
						<VInvestExpertiseSynthesisListedAssets resultData={resultData} currency={currency} />
					) : (
						<>{`View ${investExpertise.tag} not found`}</>
					)}
				</>
			) : (
				<AppLoaderContent />
			)}
		</Box>
	);
};

export default VInvestExpertiseSynthesis;
